import ContactHistory, {ApiContactHistory, ApiContactHistorySubmitData, ContactHistoryFilters, DetailContactHistoryFilters} from '@/model/ContactHistory';
import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiSortsDerived} from '../meta/odata';
import {useSettings} from '@/store/settings';
import pinia from '@/plugins/pinia';
import {
  ApiContactHistoryEmail,
  ContactHistoryEmail,
  DetailContactHistoryEmailFilters,
} from '@/model/ContactHistoryEmail';


export enum ResourceKontakt {
  Kontakt = '/Kontakt',
  Kunde = '/Kunde',
}

export enum ResourceKunde {
  Kunde = '/Kunde',
}

export enum ContactHistoryAction {
  DateiHochladen = '/{kundennr}/kontakt/{kontaktnr}/action/datei/hochladen',
  Email = '/{kundennr}/kontakt/{kontaktnr}/emails',
}


export async function getContactHistories(
    filters: ContactHistoryFilters, sorts: ApiSortsDerived<ApiContactHistory>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<ContactHistory[]>> {
  const response = await doApiRequest<ApiContactHistory[]>({
    method: Method.Get,
    resource: ResourceKontakt.Kontakt,
    module: Module.Kunde,
    filters: parseGenericFilters({...filters}),
    select: new Set([
      'KundenNr',
      'Nr',
      'KundeName',
      'AnlageDatum',
      'AnlageUhrzeit',
      'Erledigt',
      'Thema',
      'Disponent',
      'Art',
    ]),
    expand: {
      Ansprechpartner: {
        select: new Set(['Nummer', 'Nachname', 'Vorname', 'AnsprechpartnerNr']),
      },
    },
    sorts,
    limit,
    skip,
  });

  return [response.Data.map((p) => ContactHistory.fromApi(p)), response.CountAll];
}

export async function getDetailContactHistory(
    filters: DetailContactHistoryFilters,
): Promise<CombinedApiResponse<ContactHistory|null>> {
  const response = await doApiRequest<ApiContactHistory[]>({
    method: Method.Get,
    resource: ResourceKontakt.Kontakt,
    module: Module.Kunde,
    filters: parseGenericFilters({...filters/* , KundStatus: KundStatus.Active */}),
    select: new Set([
      'KundenNr',
      'KundeName',
      'Nr',
      'Art',
      'Disponent',
      'AnlageDatum',
      'AnlageUhrzeit',
      'Erledigt',
      'Thema',
      'Inhalt',
      'WiedervorlageDatum',
      'WiedervorlageUhrzeit',
      'Dateien',
      'Dokumente',
    ]),
    expand: {
      Ansprechpartner: {
        select: new Set(['*']),
      },
      Bediener: {
        select: new Set(['Bediener', 'Wiedervorlagedatum', 'Wiedervorlageuhrzeit']),
        expand: {
          Gelesen: {
            select: new Set(['Status']),
          },
          Erledigt: {
            select: new Set(['Status']),
          },
        },
      },
    },
  });

  return response.Data.length ? [ContactHistory.fromApi(response.Data[0])] : [null];
}

export async function getDetailContactHistoryEmail(
    filters: DetailContactHistoryEmailFilters,
): Promise<CombinedApiResponse<ContactHistoryEmail[]>> {
  const response = await doApiRequest<ApiContactHistoryEmail[]>({
    method: Method.Get,
    resource: ResourceKontakt.Kunde,
    module: Module.Kunde,
    action: ContactHistoryAction.Email,
    actionParameters: {
      kundennr: filters.KundenNr ?? '',
      kontaktnr: filters.KontaktNr ?? '',
    },
    filters: filters.Id ? parseGenericFilters({...{Id: filters.Id}} as DetailContactHistoryEmailFilters) : undefined,
    select: new Set([
      'Id',
      'AnlageDatum',
      'State',
      'Art',
    ]),
    expand: {
      Datei: {
        select: new Set(['Dateiname', 'DownloadToken']),
      },
    },
  });

  return [response.Data.map((p) => ContactHistoryEmail.fromApi(p)), response.CountAll];
}

export async function postContactHistory(
    KundenNr: number,
    data: ApiContactHistorySubmitData,
): Promise<ContactHistory> {
  const response = await doApiRequest({
    module: Module.Kunde,
    resource: ResourceKunde.Kunde,
    method: Method.Post,
    id: `${KundenNr}/kontakt`,
    data,
  });

  return ContactHistory.fromApi(response.Data[0]);
}

export async function putContactHistory(
    KundenNr: number,
    contactHistoryId: number,
    data: ApiContactHistorySubmitData,
): Promise<ContactHistory> {
  const response = await doApiRequest({
    module: Module.Kunde,
    resource: ResourceKunde.Kunde,
    method: Method.Put,
    id: `${KundenNr}/kontakt/${contactHistoryId}`,
    data,
  });

  return ContactHistory.fromApi(response.Data[0]);
}


export async function postContactHistoryComment(
    KundenNr: number,
    contactHistoryId: number,
    data: {
      Text: string,
    },
): Promise<ContactHistory> {
  const response = await doApiRequest({
    module: Module.Kunde,
    resource: ResourceKontakt.Kunde,
    method: Method.Post,
    id: `${KundenNr}/kontakt/${contactHistoryId}/kommentar`,
    data: {
      ...data,
      Author: useSettings(pinia).userShort,
    },
  });

  return ContactHistory.fromApi(response.Data[0]);
}

export async function postContactHistoryDocuments(
    kundennr: number,
    kontaktnr: number,
    data: FormData,
) {
  const response = await doApiRequest({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: ContactHistoryAction.DateiHochladen,
    actionParameters: {
      kundennr,
      kontaktnr,
    },
    data,
    limit: 0,
  });
  return response;
}
